import { PencilAltIcon } from "@heroicons/react/outline";
import moment, { Moment } from "moment";
import { Fragment, useEffect, useState } from "react";
import { useAsync } from "react-async";
import TaskTypeOption from "../../api/models/tasktypeoption";
import TimesheetEntryRequest from "../../api/requests/timesheetEntryRequest";
import { getTaskType } from "../../api/services/taskTypeService";
import EditEntry from "./editEntry";
import EntryForGroup from "./step2.group";
import EntryForNonStudent from "./step2.nonstudent";
import EntryForStudent from "./step2.student";
import EntryState from "./types/entryState";
import NoteModel from "./types/noteModel";
import { EntryTypes, ItemsSavedMessagesLookup, TaskTypeLookup, timeEntryMessageTimeout } from '../../common/constants';
import { getBillingTypeName, removeDuplicateMoments } from "../../common/utils";
import Step2Props from "./types/step2";
import TimesheetTaskEntries from "./types/timesheetTaskEntries";
import { batchAddUpdateTimesheetEntries } from "../../api/services/timesheetService";

/**
 * Displays UI for selecting details for the entry type and entering time for the week.  
 */
const Step2 = ({
    entryState,
    completed,
    goBack,
    cancel,
    setHasChanges,
    hasChanges,
    timesheetsAsync,
    allTaskTypes = [],
    toggle,
    setStep,
    selectedDistrictProgramOption
}: Step2Props) => {
    const [_entryState, setEntryState] = useState<EntryState>(entryState);
    const [notesRequiredForEditing, setNotesRequiredForEditing] = useState<boolean>(false);
    const [taskOptions, setTaskOptions] = useState<TaskTypeOption[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [itemsSaved, setItemsSaved] = useState<boolean>(false);
    const [showSavedMessage, setShowSavedMessage] = useState<boolean>(false);
    const [savedStudentName, setSavedStudentName] = useState('');
    const [isGroupLoaded, setIsGroupLoaded] = useState<boolean>(false);
    const [entryTasks, setEntryTasks] = useState<TimesheetTaskEntries[]>([]);
    const [studentIds, setStudentIds] = useState<number[]>([]);
    /**
     * The notes UI is handled here, we know if we are edting a note when the NoteModel is set.
     * Setting it to undefined will hide the note editor. 
     */
    const [note, setNote] = useState<NoteModel>();
    const oldNote = (note: NoteModel) => {
        note.oldNote = note.dayEntry.notes;
        setNote(note);
    }

    /**
     * Get the task type options
     */
    const taskTypeOptionsAsync = useAsync({
        deferFn: async (args: any[]) => {
            if (typeof _entryState.taskTypeId !== "number")
                return Promise.resolve();

            var tasktype = await getTaskType(args[0] ?? _entryState.taskTypeId);
            setTaskOptions(tasktype?.data?.taskType?.taskTypeOptions ?? []);
            //setTaskTypeName(tasktype?.data?.taskType?.name ?? "");
        }
    });

    /**
     * Toggles an option
     * @param option 
     * @returns 
     */
    const toggleOption = (option: string) => {
        if (typeof note?.dayEntry.notes === "undefined")
            return;

        if (!note.dayEntry.notes.includes(option))
            note.dayEntry.notes = option + ", " + note.dayEntry.notes;
        else
            note.dayEntry.notes = note.dayEntry.notes.replaceAll(option + ", ", "");

        setNote({ ...note });
    }

    /**
    * See if notes are required for edit entries
    */
    useEffect(() => {
        let isNotesRequired = false;

        entryState.timesheetEntryTasks?.map((entryTask: TimesheetTaskEntries) => {
            //Get the task type
            const taskType = allTaskTypes.find(t => t.taskTypeId === entryTask.taskTypeId);

            //If the task type was not found, escape
            if (!taskType) return entryTask;

            //If true, keep it that way, otherwise set to the notes required variable
            isNotesRequired = isNotesRequired || taskType.areNotesRequired;

            return entryTask;
        });

        setNotesRequiredForEditing(isNotesRequired);
    }, [entryState, setNotesRequiredForEditing]);


    /**
     * Saves the entry and performs necessary validations before saving.
     * @return {boolean} - Returns true if the entry is successfully saved, false otherwise.
     */
    const saveBtnClicked = async () => {
        //prevent rage clicking
        if (isSaving)
            return true; //graceful exit

        //if the keep option is active and save has been called without changes we can ignore this request. 
        if (entryState.keepOpen && hasChanges === false)
            return true; //graceful exit

        //update the note entry
        if (typeof note !== "undefined") {
            if (note.dayEntry.notes.length === 0 && _entryState.taskTypes?.find(x => x.taskTypeId === note.taskTypeId)?.areNotesRequired) {
                alert("Note cannot be empty.");
                return false;
            }

            if (note.dayEntry.notes.length > 500) {
                alert("Notes must be 500 or fewer characters. Please update your notes and try again.");
                return false;
            }

            if(entryState.timesheetEntryTasks?.length == 0){
                entryState.timesheetEntryTasks = _entryState?.timesheetEntryTasks ?? [];
            }
            var entry = entryState.timesheetEntryTasks?.find(x => x.taskTypeId === note.taskTypeId && x.billToId === note.billToId && x.isMakeupTime === note.isMakeupTime)?.entries?.find(x => moment(x.dateOfEntry).diff(moment(note.dayEntry.dateOfEntry), 'minutes') === 0);
            if (entry) {
                entry.notes = note.dayEntry.notes;
            }

            setNote(undefined);
            return false;
        }

        if (_entryState.hasValidationErrors) {
            alert("Please resolve validation issues before saving the timesheet entry");
            return false;
        }

        if (_entryState.entryTypeId === EntryTypes.Group) {
            if (!entryTasks)
                return false;

            let districtId = entryState.districtId;
            let schoolId = entryState.schoolId;
            let programId = entryState.programId && entryState.programId > 0 ? entryState.programId : null;

            let studentEntries = entryState.selectedTimesheet?.items.filter(x => studentIds.some(y => x.students?.includes(y) && x.districtId === districtId && x.schoolId === schoolId && x.programId === programId));

            let entryDates = removeDuplicateMoments(entryTasks.flatMap(x => x.entries.filter(y => y.minutes > 0)).map(x => moment(x.dateOfEntry)));

            let absentUnavailableDatesFound = false;
            let studentString = '';

            studentEntries?.forEach(x => {
                let absentUnavailableTasks = x.tasks.filter(y => y.taskTypeId === TaskTypeLookup.Absence || y.taskTypeId === TaskTypeLookup.Unavailable);
                let absentUnavailableDates = removeDuplicateMoments(absentUnavailableTasks?.flatMap(x => x.entries.filter(e => e.minutes !== undefined).map(e => moment(e.dateOfEntry)))) ?? [];

                entryDates?.forEach(y => {
                    if (absentUnavailableDates?.some(z => z.isSame(y))) {
                        absentUnavailableDatesFound = true;
                        studentString += `${studentString.length > 0 ? '\n' : ''}${x.title}: ${y.format('MM/DD')}`;
                    }
                })
            })

            if (absentUnavailableDatesFound) {
                let errorMessage = `List of students that can't be billed cause they are marked as Absent or Unavailable for the following dates :\n${studentString}`
                alert(errorMessage);
                return false;
            }
        }

        let absenceType = _entryState.timesheetEntryTasks?.find(x => x.taskTypeId === TaskTypeLookup.Absence);
        let unavailableType = _entryState.timesheetEntryTasks?.find(x => x.taskTypeId === TaskTypeLookup.Unavailable);
        let notAllowedWithAbsence = _entryState.timesheetEntryTasks?.filter(x => x.taskTypeId !== TaskTypeLookup.Absence && x.taskTypeId !== TaskTypeLookup.Unavailable && !x.taskType?.allowWhenAbsent)
        let notAllowedWithUnavailable = _entryState.timesheetEntryTasks?.filter(x => x.taskTypeId !== TaskTypeLookup.Absence && x.taskTypeId !== TaskTypeLookup.Unavailable && !x.taskType?.allowWhenUnavailable)

        if (_entryState.entryTypeId === EntryTypes.Student &&
            (absenceType || unavailableType)) {

            let absenceDays: Moment[] = absenceType ? absenceType.entries.filter(x => x.minutes != undefined).map(x => x.dateOfEntry) : [];
            let unavailableDays: Moment[] = unavailableType ? unavailableType.entries.filter(x => x.minutes != undefined).map(x => x.dateOfEntry) : [];

            let districtId = entryState.districtId;
            let schoolId = entryState.schoolId;
            let programId = entryState.programId && entryState.programId > 0 ? entryState.programId : null;

            let groupEntries = entryState.selectedTimesheet?.items.filter(x => x.students && x.students.length > 1 && studentIds.some(y => x.students?.includes(y) && x.districtId === districtId && x.schoolId === schoolId && x.programId === programId));
            let groupDates: Moment[] = groupEntries ? removeDuplicateMoments(groupEntries.flatMap(x => x.tasks).flatMap(x => x.entries.filter(x => x.minutes && x.minutes > 0)).map(x => moment(x.dateOfEntry))) : [];

            let existingGroupDateFound = false;
            let groupDatesString = '';
            groupDates.forEach(x => {
                if (absenceDays.some(y => y.isSame(x)) ||
                    unavailableDays.some(y => y.isSame(x))
                ) {
                    existingGroupDateFound = true
                    groupDatesString += `${groupDatesString.length > 0 ? '\n' : ''}${x.format("MM/DD")}`
                }
            })

            let absentAndUnavailableForTheSameDateFound = absenceDays.some(x => unavailableDays.some(y => x.isSame(y)));

            let notAllowedAbsenceFound = false;
            let notAllowedAbsenceTasksFound = ''
            notAllowedWithAbsence?.forEach(x => {
                let days = x.entries.filter(x => x.minutes > 0).map(x => x.dateOfEntry);
                if (absenceDays.some(x => days.some(y => x.isSame(y)))) {
                    notAllowedAbsenceFound = true;
                    notAllowedAbsenceTasksFound += `${notAllowedAbsenceTasksFound.length > 0 ? ', ' : ''}${x.taskType.name}`
                }
            })

            let notAllowedUnavailableFound = false;
            let notAllowedUnavailableTasksFound = ''
            notAllowedWithUnavailable?.forEach(x => {
                let days = x.entries.filter(x => x.minutes > 0).map(x => x.dateOfEntry);
                if (unavailableDays.some(x => days.some(y => x.isSame(y)))) {
                    notAllowedUnavailableFound = true;
                    notAllowedUnavailableTasksFound += `${notAllowedUnavailableTasksFound.length > 0 ? ', ' : ''}${x.taskType.name}`
                }
            })

            let errorMessage = '';

            if (absentAndUnavailableForTheSameDateFound) {
                errorMessage += "You can't have Absence task type on the same day with Unavailable task type.";
            }

            if (notAllowedAbsenceFound) {
                let message = `You can't have Absence task type on the same day with another task types: ${notAllowedAbsenceTasksFound}.`;
                errorMessage += errorMessage.length > 0 ? '\n' + message : message;
            }

            if (notAllowedUnavailableFound) {
                let message = `You can't have Unavailable task type on the same day with another task types: ${notAllowedUnavailableTasksFound}.`
                errorMessage += errorMessage.length > 0 ? '\n' + message : message;
            }

            if (existingGroupDateFound) {
                let message = `You can't have Absent or Unavailable task types on the following days since the student is billed in group entries:\n${groupDatesString}`;
                errorMessage += errorMessage.length > 0 ? '\n' + message : message;
            }

            if (absentAndUnavailableForTheSameDateFound || notAllowedAbsenceFound || notAllowedUnavailableFound || existingGroupDateFound) {
                alert(errorMessage)
                return false;
            }
        }

        let emptyTaskTypes = _entryState?.timesheetEntryTasks?.filter(x => x.taskType?.taskTypeId !== TaskTypeLookup.Unavailable && x.taskType?.taskTypeId !== TaskTypeLookup.Absence && x.entries.every(e => e.minutes === 0));
        if (emptyTaskTypes && emptyTaskTypes.length > 0) {
            let taskTypes = '';
            emptyTaskTypes.forEach(x => {
                taskTypes += `${x.taskType?.name} \n`;
            })
            alert("Some of the task types are blank. Please fill the form or uncheck the task types:\n" + taskTypes)
            return false;
        }

        //make sure notes aren't required
        if (typeof _entryState.timesheetEntryTasks !== 'undefined' && typeof _entryState.timesheetEntryTasks?.some(x => x.entries?.length > 0) !== "undefined") {
            for (var i = 0; i < _entryState.timesheetEntryTasks?.length; i++) {
                const task = _entryState.timesheetEntryTasks[i];
                
                // If taskType is not populated, try to find it from entryState.taskTypes
                if (!task?.taskType && _entryState.taskTypes) {
                    task.taskType = _entryState.taskTypes.find(t => t.taskTypeId === task.taskTypeId);
                }
                
                if (
                    (task?.taskType?.areNotesRequired === true)
                ) {
                    for (var j = 0; j < task.entries.length; j++) {
                        const entry = task.entries[j];
                        if ((entry.minutes != undefined && entry.minutes !== 0) && entry.notes === "" && !task.isRecoupment
                            || (entry.minutes != undefined && entry.notes === "" && task.taskTypeId === TaskTypeLookup.Unavailable && task.taskType?.areNotesRequired)
                        ) {
                            alert(`Notes are required for all entries of task type '${task?.taskType?.name ?? ''}'. Please enter notes for all entries and try again.`);
                            return false
                        }
                    }
                }
            }
        }

        //do some validation
        if (entryState.schoolId == -1 && entryState.allowsDistrictTime === false && (entryState.billToId ?? -1) > 0) {
            alert("Could not save entry, this district does not allow district time or the task type is missing");
            return false;
        }

        //When saving, we are now going to loop through the tasks and update all the task types at the same time
        const entries: TimesheetEntryRequest[] = [];
        entryState.timesheetEntryTasks = !entryState.timesheetEntryTasks || entryState.timesheetEntryTasks.length === 0 ? entryTasks : entryState.timesheetEntryTasks;

        //Loop though the tasks
        _entryState.timesheetEntryTasks?.map(x => {
            //Create our new entry request
            let entry: TimesheetEntryRequest = {
                timesheetId: -1,
                disciplineId: entryState.disciplineId,
                districtId: entryState.districtId ?? -1,
                entryTypeId: entryState.entryTypeId ?? -1,
                programId: entryState.programId ?? -1,
                schoolId: entryState.schoolId ?? -1,
                taskTypeId: x.taskTypeId ?? -1,
                billingTypeId: x.billToId ?? -1,
                weekEntries: [],
                studentIds: studentIds,
                startOfWeek: entryState.selectedTimesheet?.firstDayOfWeek ?? moment().toDate(),
                endOfWeek: entryState.selectedTimesheet?.lastDayOfWeek ?? moment().toDate(),
                recoupment: x.isRecoupment ?? false,
                makeupTime: x.isMakeupTime ?? false
            }

            if (typeof entryState.studentId !== "undefined" && entryState.studentId !== -1) {
                entry.studentIds.push(entryState.studentId!);
            } else {
                if (!entry.studentIds || entry.studentIds.length === 0) {
                    entryState.selectedStudents?.forEach(student => entry.studentIds.push(student.studentId));
                }
            }

            //Loop through the entries and generate our week entries for this task type
            x.entries.forEach((dayEntry) => {
                //ignore entries with no time
                if (x.taskTypeId !== TaskTypeLookup.Absence && x.taskTypeId !== TaskTypeLookup.Unavailable && (dayEntry.minutes == undefined || dayEntry.minutes < 0) && dayEntry.timesheetEntryId == -1)
                    return;

                var date = new Date(dayEntry.date ?? dayEntry.dateOfEntry);
                var timezoneOffset = date.getTimezoneOffset();
                let utc = moment(date).add(-timezoneOffset, 'm').toDate();

                entry.weekEntries.push({
                    entryId: Number(dayEntry.timesheetEntryId ?? dayEntry.entryId ?? -1),
                    minutes: dayEntry.minutes ?? undefined,
                    notes: dayEntry.notes,
                    // dateOfEntry: moment(dayEntry.date ?? dayEntry.dateOfEntry).toDate(),
                    dateOfEntry: utc,
                    timesheetEntryFlags: [],
                    recoupment: dayEntry.recoupment ?? dayEntry.isRecoupment ?? false,
                    makeupTime: x.isMakeupTime ?? dayEntry.isMakeupTime ?? dayEntry.makeupTime ?? false,
                    markAsDeleted: dayEntry.markAsDeleted,
                })
            });

            entries.push(entry);
        });

        if (_entryState.entryTypeId === EntryTypes.Student) {
            let studentId = _entryState.studentId ?? -1;
            let selectedTasks = _entryState.selectedTimesheet?.items.find(x => x.students?.length === 1 && x.students.includes(studentId))?.tasks;
            let selectedAbsence = selectedTasks?.find(x => x.taskTypeId === TaskTypeLookup.Absence);
            if (!entries.some(x => x.taskTypeId === TaskTypeLookup.Absence) && selectedAbsence) {
                let entry: TimesheetEntryRequest = {
                    timesheetId: selectedAbsence.entries?.find(x => x.timesheetId && x.timesheetId > 0)?.timesheetId ?? -1,
                    disciplineId: entryState.disciplineId,
                    districtId: entryState.districtId ?? -1,
                    entryTypeId: entryState.entryTypeId ?? -1,
                    programId: entryState.programId ?? -1,
                    schoolId: entryState.schoolId ?? -1,
                    taskTypeId: TaskTypeLookup.Absence,
                    billingTypeId: -1,
                    weekEntries: [],
                    studentIds: [studentId],
                    startOfWeek: entryState.selectedTimesheet?.firstDayOfWeek ?? moment().toDate(),
                    endOfWeek: entryState.selectedTimesheet?.lastDayOfWeek ?? moment().toDate(),
                    recoupment: false,
                    makeupTime: false
                }

                selectedAbsence.entries.forEach(x => {
                    var date = new Date(x.date ?? x.dateOfEntry);
                    var timezoneOffset = date.getTimezoneOffset();
                    let utc = moment(date).add(-timezoneOffset, 'm').toDate();

                    entry.weekEntries.push({
                        entryId: Number(x.timesheetEntryId ?? x.entryId ?? -1),
                        minutes: undefined,
                        notes: x.notes,
                        dateOfEntry: utc,
                        timesheetEntryFlags: [],
                        recoupment: false,
                        makeupTime: false,
                        markAsDeleted: true,
                    })
                })

                entries.push(entry);
            }

            let selectedUnavailable = selectedTasks?.find(x => x.taskTypeId === TaskTypeLookup.Unavailable);
            if (!entries.some(x => x.taskTypeId === TaskTypeLookup.Unavailable) && selectedUnavailable) {
                let entry: TimesheetEntryRequest = {
                    timesheetId: selectedUnavailable.entries?.find(x => x.timesheetId && x.timesheetId > 0)?.timesheetId ?? -1,
                    disciplineId: entryState.disciplineId,
                    districtId: entryState.districtId ?? -1,
                    entryTypeId: entryState.entryTypeId ?? -1,
                    programId: entryState.programId ?? -1,
                    schoolId: entryState.schoolId ?? -1,
                    taskTypeId: TaskTypeLookup.Unavailable,
                    billingTypeId: -1,
                    weekEntries: [],
                    studentIds: [studentId],
                    startOfWeek: entryState.selectedTimesheet?.firstDayOfWeek ?? moment().toDate(),
                    endOfWeek: entryState.selectedTimesheet?.lastDayOfWeek ?? moment().toDate(),
                    recoupment: false,
                    makeupTime: false
                }

                selectedUnavailable.entries.forEach(x => {
                    var date = new Date(x.date ?? x.dateOfEntry);
                    var timezoneOffset = date.getTimezoneOffset();
                    let utc = moment(date).add(-timezoneOffset, 'm').toDate();

                    entry.weekEntries.push({
                        entryId: Number(x.timesheetEntryId ?? x.entryId ?? -1),
                        minutes: undefined,
                        notes: x.notes,
                        dateOfEntry: utc,
                        timesheetEntryFlags: [],
                        recoupment: false,
                        makeupTime: false,
                        markAsDeleted: true,
                    })
                })

                entries.push(entry);
            }
        }

        //Set saving indicator
        setIsSaving(true);
        //Send our request
        var result = await batchAddUpdateTimesheetEntries(entries);

        //Set the save state hooks for our messages
        setIsSaving(false);
        setIsSaved(true);

        //Reset the modal
        resetEntries();

        //check if there was an issue saving the entry
        if (result == null || result.status !== 200 || result.data === null || result.data === undefined) {
            window.alert("An issue occured while saving you're entry.");
            return false;
        }

        //Update the items saved message
        if (!entryState.editingEntry && result.data !== undefined) {
            //Set any data the save message will need
            setSavedStudentName(_entryState.studentName ?? '');

            //Refresh the data and go back to the first step of our current entry type
            toggle(true, true);

            //Set a timer so that the message is displayed after the toggle refreshes
            setTimeout(() => {
                //Show our saved items message
                setItemsSaved(true);

                //Hide the message after our timeout
                setTimeout(() => {
                    setItemsSaved(false);
                    setSavedStudentName('');
                    setShowSavedMessage(true);
                }, timeEntryMessageTimeout);
            }, 1000);
        } else {
            completed(_entryState); //tell the parent that the timesheet entry is complete
        }

        if (!_entryState?.editingEntry)
            setStep(1);
        //Ensure the modal thinks we have no changes (save button disabled)
        setHasChanges(false);
        return true;
    }

    /**
     * Resets the entries after saving
     * @return {void} There is no return value.
     */
    const resetEntries = (): void => {
        setStudentIds([]);
        setEntryTasks([]);
        setEntryState(current => {
            current.timesheetEntries = [];
            current.timesheetEntryTasks = [];
            current.taskTypeId = -1;
            current.billToId = -1;
            return current;
        })
    }


    /**
     * Cancels the current operation and performs the necessary cleanup.
     * @return {void} No return value.
     */
    const cancelBtnClicked = () => {
        //if editing a note, switch back to the entry screens
        if (typeof note !== "undefined") {
            note.dayEntry.notes = note.oldNote!;
            setNote(undefined);
            return;
        }

        cancel(entryState);
    }

    return (
        <Fragment>
            {/*isLoading ? <Spinner /> : <>*/}
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                {!note && (
                    <Fragment>
                        {/* Entry Details displayed at the top */}
                        <div className="text-xs">{_entryState.districtName}</div>
                        <div className="text-xs">{_entryState.schoolName}</div>
                        {typeof _entryState.programId !== "undefined" && _entryState.programId > 0 && (
                            <Fragment>
                                <div className="bg-purple-500 text-white inline-block px-2 py-0.5 rounded-lg text-sm">{_entryState.programName}</div>
                                <br />
                            </Fragment>
                        )}
                        {!_entryState.editingEntry && (
                            <div className="relative inline mt-0 mb-1 text-xs font-bold text-blue-500 cursor-pointer bottom-1" onClick={() => { goBack(); }}>
                                <PencilAltIcon
                                    className="inline-block w-4 mr-1"
                                />
                                Change
                            </div>
                        )}
                        <br />

                        {/** Student Entry */}
                        {_entryState?.entryTypeId === 1 && !_entryState?.editingEntry && (
                            <EntryForStudent
                                entryState={_entryState}
                                saveEntry={saveBtnClicked}
                                setEntryState={(m) => { setEntryState(m); }}
                                editNote={(n) => { taskTypeOptionsAsync.run(n.taskTypeId); oldNote(n); }}
                                setHasChanges={setHasChanges}
                                hasChanges={hasChanges}
                                setIsSaved={setIsSaved}
                                isSaved={isSaved}
                                toggle={toggle}
                                setStep={setStep}
                                timesheetsAsync={timesheetsAsync}
                                entryTasks={entryTasks}
                                setEntryTasks={setEntryTasks}
                                studentIds={studentIds}
                                setStudentIds={setStudentIds}
                                setShowSavedMessage={(show) => setShowSavedMessage(show)}
                                selectedDistrictProgramOption={selectedDistrictProgramOption}
                            />
                        )}

                        {/** Non-Student Entry */}
                        {_entryState?.entryTypeId === 2 && !_entryState?.editingEntry && (
                            <EntryForNonStudent
                                entryState={_entryState}
                                saveEntry={saveBtnClicked}
                                setEntryState={(m) => { setEntryState(m); }}
                                editNote={(n) => { taskTypeOptionsAsync.run(n.taskTypeId); oldNote(n); }}
                                setHasChanges={setHasChanges}
                                hasChanges={hasChanges}
                                setIsSaved={setIsSaved}
                                isSaved={isSaved}
                                toggle={toggle}
                                setStep={setStep}
                                timesheetsAsync={timesheetsAsync}
                                selectedDistrictProgramOption={selectedDistrictProgramOption}
                            />
                        )}

                        {/** Group Entry */}
                        {_entryState?.entryTypeId === 3 && !_entryState?.editingEntry && (
                            <EntryForGroup
                                entryState={_entryState}
                                saveEntry={saveBtnClicked}
                                setEntryState={(m) => { setEntryState(m); }}
                                editNote={(n) => { taskTypeOptionsAsync.run(n.taskTypeId); oldNote(n); }}
                                setHasChanges={setHasChanges}
                                hasChanges={hasChanges}
                                setIsSaved={setIsSaved}
                                isSaved={isSaved}
                                toggle={toggle}
                                setStep={setStep}
                                timesheetsAsync={timesheetsAsync}
                                isGroupLoaded={isGroupLoaded}
                                setIsGroupLoaded={setIsGroupLoaded}
                                entryTasks={entryTasks}
                                setEntryTasks={setEntryTasks}
                                studentIds={studentIds}
                                setStudentIds={setStudentIds}
                                setShowSavedMessage={(show) => setShowSavedMessage(show)}
                                selectedDistrictProgramOption={selectedDistrictProgramOption}
                            />
                        )}

                        {/** Edit Entries */}
                        {_entryState?.editingEntry && (
                            <EditEntry
                                entryState={_entryState}
                                setEntryState={(m) => { setEntryState(m); }}
                                editNote={(n) => {
                                    taskTypeOptionsAsync.run(n.taskTypeId);
                                    oldNote(n);
                                }}
                                setHasChanges={setHasChanges}
                            />
                        )}
                    </Fragment>
                )}

                {note && (
                    <Fragment>
                        <div className="text-lg font-bold">{moment(note.dayEntry.date ?? note.dayEntry.dateOfEntry).format("M/D dddd")}</div>
                        <div className="mb-2 text-xs font-bold">Notes</div>

                        <div className="font-bold">Task Type</div>
                        <div className="mb-4">
                            {allTaskTypes ? allTaskTypes.find(x => x.taskTypeId === note.taskTypeId)?.name ?? '' : ''}
                            {note.billToId && note.billToId > 0 ? ` - ${getBillingTypeName(note.billToId)}` : ''}
                            {note.isRecoupment ? ' (Recoupment)' : ''}
                            {note.isMakeupTime ? ' (Makeup Time)' : ''}
                        </div>

                        {taskTypeOptionsAsync.isLoading && (
                            <div> Loading Options </div>
                        )}

                        {taskOptions.length > 0 && (
                            <div className="mb-2">
                                <div className="font-bold">Toggle options</div>
                                {taskOptions.map((option) => (
                                    <div className="ml-2 cursor-pointer text-ptsblue-normal hover:underline" key={`to_${option.taskTypeOptionId}`} onClick={() => toggleOption(option.name)}>{option.name}</div>
                                ))}
                            </div>
                        )}

                        <div className="font-bold">Notes</div>
                        <textarea
                            className={note.dayEntry.notes.length > 500 ? "h-40 text-red-700" : "h-40"}
                            disabled={note.taskTypeId === TaskTypeLookup.Indirect}
                            autoFocus
                            value={note.dayEntry.notes}
                            onChange={(e) => { note.dayEntry.notes = e.target.value; setNote({ ...note }); }}>
                        </textarea>
                    </Fragment>
                )}
            </div>

            {/* Items Saved Message */}
            {itemsSaved && (<div className={`grid grid-cols-5`}>
                <span></span>
                <span className="col-span-3 text-center text-green-800">
                    {entryState.entryTypeId === EntryTypes.Student && <>{ItemsSavedMessagesLookup.Student.replace(/\{\{STUDENT_NAME\}\}/g, savedStudentName ?? entryState.studentName ?? '')}</>}
                    {entryState.entryTypeId === EntryTypes.Group && <>{ItemsSavedMessagesLookup.Group}</>}
                    {entryState.entryTypeId === EntryTypes.NonStudent && <>{ItemsSavedMessagesLookup.NonStudent}</>}

                    {/* Default */}
                    {entryState.entryTypeId !== EntryTypes.Student &&
                        entryState.entryTypeId !== EntryTypes.Group &&
                        entryState.entryTypeId !== EntryTypes.NonStudent && <>{ItemsSavedMessagesLookup.Default}</>}
                </span>
                <span></span>
            </div>)}

            {/*Show saved message*/}
            {showSavedMessage && (<div className={`grid grid-cols-5`}>
                <span></span>
                <span className="col-span-3 text-center text-green-800">
                    Saved
                </span>
                <span></span>
            </div>)}

            {/* Is Saving Message */}
            {isSaving && (<div className={`grid grid-cols-3`}>
                <span></span>
                <span className="col-span-3 text-center text-green-800">Saving...</span>
                <span></span>
            </div>)}

            {/* Bottom Button Bar (Save, Go Back, Cancel) */}
            <div className='grid grid-cols-5 px-4 py-3 bg-gray-50 sm:px-6'>
                {/** Go Back */}
                {entryState.editingEntry === false && (
                    <button
                        type="button"
                        className="inline-flex justify-center w-full px-4 py-2 mt-3 ml-0 mr-auto text-base font-medium text-gray-700 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={() => goBack()}>
                        Go Back
                    </button>
                )}
                <div className="grid-spacer"></div>

                {/** Save */}
                <button
                    type='button'
                    className='inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-ptsblue-normal hover:bg-ptsblue-dark focus:outline-none sm:ml-3 sm:w-auto sm:text-sm'
                    onClick={saveBtnClicked}
                    disabled={!hasChanges}
                >
                    {isSaving && ("Saving")}
                    {!isSaving && ("Save")}
                </button>
                <div className="grid-spacer"></div>

                {/** Cancel/Close */}
                <button
                    type='button'
                    className='inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
                    onClick={cancelBtnClicked}
                >
                    {entryState.keepOpen === true && ("Close")}
                    {entryState.keepOpen === false && ("Cancel")}
                </button>
            </div>
            {/*</>}*/}
        </Fragment>
    );
}

export default Step2;